import { useEffect, useState } from "react";
import { Check, Sparkles } from "lucide-react";
import Reveal, { SectionHead } from "./Reveal";
import { scrollToId, getCohort } from "@/lib/api";

const SETUP = [
  {
    name: "The Digital Ministry Visibility Audit™", price: "Free", alt: "or $297 credited toward setup",
    best: "Your low-friction first step",
    features: ["Review of website, Google presence, social profiles & visitor journey",
      "Digital Visibility Score from 1–10", "Identification of your “invisibility gaps”",
      "Top two platform priorities", "A 90-day content & outreach roadmap", "30-minute leadership debrief"],
    cta: "Book My Free Audit",
  },
  {
    name: "The Kingdom Visibility Launch™", price: "$3,500", alt: "21–30 day buildout",
    best: "Churches with no presence or inactive accounts", featured: true,
    features: ["Facebook, Instagram & YouTube setup/optimization", "Bios, banners, links & contact buttons",
      "Plan Your Visit landing page", "Visitor connection + prayer-request forms",
      "30-day content calendar + 15–20 branded graphics", "Staff/volunteer training + 60-min launch call"],
    cta: "Start My 30-Day Launch",
  },
];

const MONTHLY = [
  {
    name: "The Weekly Digital Pulpit™", price: "$797", per: "/month",
    best: "Churches with 50–200 attendees",
    features: ["Facebook + Instagram management", "3 posts per week", "2 sermon-clip Reels per month",
      "Event & service graphics", "Monthly analytics report", "Monthly 30-min strategy call"],
  },
  {
    name: "The Kingdom Visibility Growth System™", price: "$1,497", per: "/month",
    best: "Churches with 150–500 attendees",
    features: ["Facebook + Instagram + YouTube management", "4–5 posts weekly + 4–8 Reels/Shorts",
      "Weekly YouTube sermon upload & optimization", "Event campaigns & sermon repurposing",
      "Monthly analytics + strategy meeting", "Quarterly digital ministry growth review"],
  },
  {
    name: "The Digital Sanctuary Partnership™", price: "$2,997", per: "/month",
    best: "Churches of 300–1,000 members",
    features: ["Everything in the Growth System", "Website maintenance & monthly updates",
      "12–16 monthly Reels/Shorts + livestream strategy", "Email/text campaign support",
      "Seasonal outreach campaigns (Easter, Christmas…)", "Paid-ad strategy + priority turnaround"],
  },
];

export default function Pricing() {
  const [cohort, setCohort] = useState(null);
  useEffect(() => {
    getCohort().then((r) => setCohort(r.data)).catch(() => {});
  }, []);
  return (
    <section id="packages" data-testid="packages-section" className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <SectionHead eyebrow="The Offer Suite" title="Digital Ministry Infrastructure. Ongoing Kingdom Visibility."
          sub="We do not sell “posting.” We build the digital doorway your church should have had years ago — then keep it alive Monday through Saturday." />

        <Reveal delay={0.1}>
          <div className="mt-12 rounded-lg border border-crimson/60 bg-gradient-to-r from-crimson/25 via-crimson/10 to-transparent p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4"
            data-testid="founding-cohort-banner">
            <Sparkles className="text-gold shrink-0" size={28} />
            <div>
              <p className="font-display text-lg sm:text-xl uppercase tracking-tight text-white">
                Founding Church Partner Cohort — <span className="gold-text">Only 5 Churches</span>
              </p>
              <p className="mt-1 text-sm text-slate-300">
                Full Kingdom Visibility Launch from <span className="text-gold font-semibold">$2,997</span>
                <span className="line-through text-slate-500 ml-2">$3,500</span> — plus founding-partner pricing on monthly management.
              </p>
              {cohort && (
                <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="cohort-counter">
                  <span className="flex gap-1.5">
                    {Array.from({ length: cohort.total }).map((_, i) => (
                      <span key={i}
                        className={`w-3 h-3 rounded-full ${i < cohort.claimed
                          ? "bg-white/15" : "bg-gold shadow-[0_0_10px_rgba(212,175,55,0.65)]"}`} />
                    ))}
                  </span>
                  <span className="text-xs font-bold tracking-[0.18em] uppercase text-gold" data-testid="cohort-counter-text">
                    {cohort.remaining} of {cohort.total} Founding Partner spots remaining
                  </span>
                </div>
              )}
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          {SETUP.map((o, i) => (
            <Reveal key={o.name} delay={i * 0.08}>
              <div data-testid={`package-card-${i}`}
                className={`h-full rounded-lg p-8 flex flex-col transition-all duration-300 hover:-translate-y-1.5 ${o.featured
                  ? "border-2 border-gold bg-gold/5 shadow-[0_0_40px_rgba(212,175,55,0.15)]"
                  : "border border-white/10 bg-navy-surface hover:border-gold/50"}`}>
                {o.featured && (
                  <span className="self-start mb-4 rounded-full bg-gold px-3 py-1 text-[9px] font-bold tracking-[0.25em] uppercase text-navy">
                    Recommended Start
                  </span>
                )}
                <h3 className="font-display text-xl sm:text-2xl uppercase tracking-tight text-white">{o.name}</h3>
                <p className="mt-1 text-[11px] tracking-[0.18em] uppercase text-slate-400">{o.best}</p>
                <div className="mt-5 flex items-baseline gap-3">
                  <span className="font-display text-4xl gold-text">{o.price}</span>
                  <span className="text-xs text-slate-400">{o.alt}</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {o.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-200">
                      <Check size={15} className="text-gold mt-0.5 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <button data-testid={`package-cta-${i}`} onClick={() => scrollToId("audit")}
                  className={`mt-8 rounded-full px-6 py-3.5 text-[11px] font-bold tracking-[0.18em] uppercase transition-all hover:scale-[1.03] ${o.featured
                    ? "bg-gold text-navy hover:bg-gold-hover hover:shadow-[0_0_30px_rgba(212,175,55,0.4)]"
                    : "border border-gold/50 text-gold hover:bg-gold hover:text-navy"}`}>
                  {o.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-6">
          {MONTHLY.map((o, i) => (
            <Reveal key={o.name} delay={i * 0.08}>
              <div data-testid={`monthly-package-card-${i}`}
                className="h-full rounded-lg border border-white/10 bg-navy-surface p-7 flex flex-col transition-all duration-300 hover:border-gold/60 hover:-translate-y-1.5">
                <h3 className="font-display text-lg uppercase tracking-tight text-white leading-snug">{o.name}</h3>
                <p className="mt-1 text-[10px] tracking-[0.18em] uppercase text-slate-400">{o.best}</p>
                <div className="mt-4 flex items-baseline">
                  <span className="font-display text-3xl gold-text">{o.price}</span>
                  <span className="text-xs text-slate-400 ml-1">{o.per}</span>
                </div>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {o.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[13px] text-slate-300">
                      <Check size={14} className="text-gold mt-0.5 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <button data-testid={`monthly-package-cta-${i}`} onClick={() => scrollToId("audit")}
                  className="mt-7 rounded-full border border-gold/50 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-gold transition-all hover:bg-gold hover:text-navy">
                  Book Your Audit First
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-10 text-xs text-slate-500 leading-relaxed max-w-3xl" data-testid="packages-note">
            Three-month minimum on monthly management — one month is not enough to establish consistency and prove
            the content rhythm. Your church always owns its own Facebook, Instagram, YouTube, domain, website, and
            ad accounts. We never promise attendance growth; we promise visibility, professional setup, consistent
            content, and clear reporting.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
